/* Bumper & Body Parts theme controller.
 *
 * One file, one IIFE. Sections:
 *   1. utilities, money, storage
 *   2. catalog data: fitment index + search meta loaders, title decomposition
 *   3. vehicle state (saved vehicle + garage), selectors, chips, banners
 *   4. product renderers (card + dense row) shared by every client-side list
 *   5. listing engine: collection client mode, fitment-aware search, predictive search
 *   6. product page, cart drawer, cart page, sheets, home rail, recommendations
 *
 * Data conventions it relies on:
 *   title   = "YYYY YYYY ... Make Model [Trims] Part ... [Side] [Finish]"
 *   tags    = "Model YYYY-YYYY" (optionally "Make Model YYYY-YYYY"), plus functional
 *             tags such as ship-freight (settings.freightTag)
 *   fitment-index.json / search-meta.json are generated by the store kit's
 *   fitment-updater from the live catalog (plus modelTrims / handleTrims).
 */
(function () {
  'use strict';

  /* =====================================================================
     1. Utilities
     ===================================================================== */
  var BBP = window.BBP || {};
  var S = BBP.settings || {};
  var R = BBP.routes || {};
  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function icon(name, size) { size = size || 16; return '<svg class="bbp-icon" width="' + size + '" height="' + size + '" aria-hidden="true" focusable="false"><use href="#i-' + name + '"></use></svg>'; }
  function money(cents) {
    var fmt = S.moneyFormat || '${{amount}}';
    var n = Number(cents) || 0;
    var amount = (n / 100).toFixed(2);
    var noDec = String(Math.round(n / 100));
    var comma = amount.replace('.', ',');
    var withThousands = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*amount\s*\}\}/g, withThousands)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec)
      .replace(/<[^>]+>/g, '');
  }
  var _mem = {};
  var Store = {
    get: function (k) { try { var v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch (e) { return _mem[k] || null; } },
    set: function (k, v) { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v)); } catch (e) { _mem[k] = v; } }
  };
  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }
  function pool(items, worker, limit) {
    var i = 0, out = new Array(items.length);
    function next() { if (i >= items.length) return Promise.resolve(); var ix = i++; return Promise.resolve(worker(items[ix], ix)).then(function (r) { out[ix] = r; return next(); }); }
    var runners = []; for (var k = 0; k < Math.min(limit || 6, items.length); k++) runners.push(next());
    return Promise.all(runners).then(function () { return out; });
  }

  /* =====================================================================
     2. Catalog data
     ===================================================================== */
  var idxPromise = null, metaPromise = null, metaByHandle = null;
  function loadIndex() {
    if (idxPromise) return idxPromise;
    idxPromise = fetch(BBP.fitmentIndexUrl || '/assets/fitment-index.json').then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    return idxPromise;
  }
  function loadMeta() {
    if (metaPromise) return metaPromise;
    metaPromise = fetch(BBP.searchMetaUrl || '/assets/search-meta.json').then(function (r) { return r.ok ? r.json() : null; })
      .then(function (arr) { if (!arr) return null; metaByHandle = {}; arr.forEach(function (m) { metaByHandle[m.h] = m; }); return metaByHandle; })
      .catch(function () { return null; });
    return metaPromise;
  }

  var HIDDEN_TAGS = (S.hiddenTags || ['ship-freight']).map(function (t) { return String(t).toLowerCase(); });
  var FREIGHT_TAG = String(S.freightTag || 'ship-freight').toLowerCase();
  var TAG_RE = /^(.+?)\s+(\d{4})(?:\s*-\s*(\d{4}))?$/;
  var BRANDS = ['Mercedes-Benz', 'Mercedes', 'Freightliner', 'Chevrolet', 'Chevy', 'Mitsubishi', 'Infiniti', 'Genesis', 'Nissan', 'Toyota', 'Lexus', 'Honda', 'Acura', 'Dodge', 'Ford', 'Lincoln', 'Ram', 'GMC', 'BMW', 'Jeep', 'Cadillac', 'Buick', 'Chrysler', 'Subaru', 'Mazda', 'Hyundai', 'Kia', 'Volkswagen', 'Audi', 'Volvo'];
  var BRAND_DISPLAY = { Chevy: 'Chevrolet', Mercedes: 'Mercedes-Benz' };
  var MAKE_DISPLAY = { Dodge: 'Dodge / Ram' };
  var SIZE_RE = /\b(1500|2500|3500|4500|150|250|350|350HD|550)\b/g;
  var TRIM_RE = /\b(EX-L|EX|LX|LE|SE|XLE|XSE|LS|LT|SLT|SLE|RS|WT|AT4|XL|XLT|TRD|SV|SR|SL|S|L|Hybrid|HYBRID|Limited|LIMITED|Platinum|PLATINUM|Touring|Nismo|NISMO)\b/g;
  var FINISHES = [['Gloss Black', 'Gloss black'], ['Textured Black', 'Textured black'], ['Matte Black', 'Matte black'], ['Textured', 'Textured'], ['Chrome', 'Chrome'], ['Primed', 'Primed'], ['Paintable', 'Paintable'], ['Black', 'Black']];

  function collapseYears(years) {
    var nums = years.map(Number).filter(function (n) { return !isNaN(n); }).sort(function (a, b) { return a - b; });
    var groups = [];
    nums.forEach(function (n) { var g = groups[groups.length - 1]; if (g && n === g[1] + 1) g[1] = n; else if (!g || n !== g[1]) groups.push([n, n]); });
    return groups.map(function (g) { return g[0] === g[1] ? String(g[0]) : g[0] + '–' + g[1]; }).join(', ');
  }
  function fitTags(tags) { return (tags || []).filter(function (t) { return HIDDEN_TAGS.indexOf(String(t).toLowerCase()) === -1 && TAG_RE.test(String(t).trim()); }); }
  function isFreight(tags) { return (tags || []).some(function (t) { return String(t).toLowerCase() === FREIGHT_TAG; }); }

  // Title decomposition. Mirrors snippets/product-title.liquid so a client-rendered card
  // reads exactly like a server-rendered one.
  function parseTitle(title, tags, productType) {
    var raw = String(title || '').replace(/\s+/g, ' ').trim();
    var s = ' ' + raw.replace(/^Fits\s+/i, '').replace(/\s(to|To|TO)\s(?=(19|20)\d{2})/g, '-') + ' ';
    var years = [];
    s = s.replace(/\b(19[89]\d|20[0-4]\d)(?:\s*-\s*(19[89]\d|20[0-4]\d))?\b/g, function (m, a, b) {
      var x = +a, y = b ? +b : +a; if (y >= x && y - x < 40) for (var k = x; k <= y; k++) years.push(k); return ' ';
    });
    var makes = [];
    BRANDS.forEach(function (b) {
      var re = new RegExp('\\b' + b.replace('-', '\\-') + '\\b', 'i');
      if (re.test(s)) { var d = BRAND_DISPLAY[b] || b; if (makes.indexOf(d) === -1) makes.push(d); s = s.replace(new RegExp('\\b' + b.replace('-', '\\-') + '\\b', 'gi'), ' '); }
    });
    var models = [], vehicles = [];
    fitTags(tags).forEach(function (t) {
      var m = TAG_RE.exec(String(t).trim()); if (!m) return;
      var name = m[1].trim(), mk = '';
      var fw = name.split(/\s+/)[0];
      var brand = BRANDS.filter(function (b) { return b.toLowerCase() === fw.toLowerCase(); })[0];
      if (brand) { mk = BRAND_DISPLAY[brand] || brand; name = name.slice(fw.length).trim(); }
      if (!name) return;
      if (models.indexOf(name) === -1) models.push(name);
      var label = ((mk || makes[0] || '') + ' ' + name).trim();
      if (vehicles.indexOf(label) === -1) vehicles.push(label);
    });
    models.slice().sort(function (a, b) { return b.length - a.length; }).forEach(function (m) {
      var re = new RegExp('\\b' + m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+') + '\\b', 'gi');
      s = s.replace(re, ' ');
      // A title may name only part of a multi-word model ("Promaster" for "ProMaster Van"),
      // so each word goes too. Same rule as snippets/product-title.liquid.
      m.split(/\s+/).forEach(function (w) {
        if (w.length > 1 && !/^\d+$/.test(w)) s = s.replace(new RegExp('\\b' + w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'gi'), ' ');
        // "HR-V" is also written "HRV" in titles.
        if (w.indexOf('-') !== -1) s = s.replace(new RegExp('\\b' + w.replace(/-/g, '') + '\\b', 'gi'), ' ');
      });
    });
    var sizes = [];
    if (/\b(ProMaster|Promaster|Sprinter|Transit|Silverado|Sierra|Pickup|Ram|Van)\b/i.test(raw) || /\b(1500|2500|3500)\b/.test(raw)) {
      s = s.replace(SIZE_RE, function (m) { if (sizes.indexOf(m) === -1) sizes.push(m); return ' '; });
      s = s.replace(/\bVan\b/g, ' ');
    }
    s = s.replace(TRIM_RE, ' ');
    var side = null;
    if (/\b(Set|Pair)\b/i.test(s) || (/\b(Left|LH|Driver)\b/i.test(s) && /\b(Right|RH|Passenger)\b/i.test(s))) side = 'PAIR';
    else if (/\b(Right|RH|Passenger)\b/i.test(s)) side = 'R';
    else if (/\b(Left|LH|Driver)\b/i.test(s)) side = 'L';
    s = s.replace(/\b(Left\s*Driver\s*(&|and)\s*Right\s*Passenger\s*(Side\s*)?(Set|Pair)?|Left\s*(&|and)\s*Right\s*(Side\s*)?(Set|Pair)?|Driver\s*(&|and)\s*Passenger\s*(Side\s*)?(Set|Pair)?|Right\s*Passenger\s*Side|Left\s*Driver\s*Side|Right\s*Passenger\s*RH|Left\s*Driver\s*LH|Passenger\s*Side|Driver\s*Side|Right\s*Passenger|Left\s*Driver|Left\s*Side|Right\s*Side)\b/gi, ' ');
    s = s.replace(/\b(Right|Left|RH|LH|Passenger|Driver|Set|Pair)\b/gi, ' ');
    var finish = null;
    for (var i = 0; i < FINISHES.length; i++) {
      var re2 = new RegExp('\\b' + FINISHES[i][0] + '\\b', 'i');
      if (re2.test(s)) { finish = FINISHES[i][1]; s = s.replace(new RegExp('\\b' + FINISHES[i][0] + '\\b', 'gi'), ' '); break; }
    }
    var part = s.replace(/\s+/g, ' ').replace(/^[\s,&\-\/]+|[\s,&\-\/]+$/g, '').trim();
    if (!part) part = productType || raw;
    if (sizes.length && vehicles.length) vehicles = vehicles.map(function (v) { return v + ' ' + sizes.join(' '); });
    if (!vehicles.length && makes.length) vehicles = [makes.join(' / ') + (sizes.length ? ' ' + sizes.join(' ') : '')];
    return { years: years, yearsLabel: collapseYears(years), makes: makes, models: models, vehicles: vehicles, side: side, finish: finish, part: part, raw: raw };
  }

  /* =====================================================================
     3. Vehicle state
     ===================================================================== */
  var state = {
    vehicle: Store.get('bbp_vehicle'),
    garage: Store.get('bbp_garage') || [],
    fitSet: null, fitKey: '',
    view: Store.get('bbp_view') || 'grid'
  };
  function vehicleId(v) { return [v.year, v.make, v.model, v.trim || ''].join('|'); }
  function vehicleLabel(v, short) {
    if (!v) return '';
    var mk = short ? v.make : (MAKE_DISPLAY[v.make] || v.make);
    return [v.year, mk, v.model, v.trim].filter(Boolean).join(' ');
  }
  function saveVehicle(v) {
    if (!v || !v.year || !v.make) return;
    v = { year: String(v.year), make: v.make, model: v.model || '', trim: v.trim || '' };
    state.vehicle = v; Store.set('bbp_vehicle', v);
    var id = vehicleId(v);
    if (!state.garage.some(function (g) { return vehicleId(g) === id; })) { state.garage.unshift(v); state.garage = state.garage.slice(0, 8); Store.set('bbp_garage', state.garage); }
    state.fitSet = null;
  }
  function clearVehicle() { state.vehicle = null; Store.set('bbp_vehicle', null); state.fitSet = null; }
  function deleteGarage(id) {
    state.garage = state.garage.filter(function (g) { return vehicleId(g) !== id; }); Store.set('bbp_garage', state.garage);
    if (state.vehicle && vehicleId(state.vehicle) === id) clearVehicle();
  }
  function fitSetFor(idx, v) {
    if (!idx || !v) return new Set();
    var key = vehicleId(v);
    if (state.fitSet && state.fitKey === key) return state.fitSet;
    var mm = (idx.fitment[v.year] || {})[v.make] || {};
    var hs = v.model ? (mm[v.model] || []) : Object.keys(mm).reduce(function (a, m) { return a.concat(mm[m]); }, []);
    if (v.trim && idx.handleTrims) {
      var t = v.trim.toUpperCase();
      hs = hs.filter(function (h) { var ht = idx.handleTrims[h]; return !ht || ht.some(function (x) { return String(x).toUpperCase() === t; }); });
    }
    state.fitSet = new Set(hs); state.fitKey = key;
    return state.fitSet;
  }
  function fitStatus(idx, handle) {
    if (!state.vehicle) return 'unknown';
    if (!idx) return 'unknown';
    return fitSetFor(idx, state.vehicle).has(handle) ? 'fits' : 'nofit';
  }

  // Vehicle selectors: every [data-vs] block, any context.
  function initSelectors(root) {
    $$('[data-vs]', root).forEach(function (vs) {
      if (vs.dataset.vsInit === '1') return; vs.dataset.vsInit = '1';
      var yearSel = $('[data-vs-year]', vs), makeSel = $('[data-vs-make]', vs), modelSel = $('[data-vs-model]', vs), trimSel = $('[data-vs-trim]', vs), trimStep = $('[data-vs-step-trim]', vs), submit = $('[data-vs-submit]', vs);
      var strings = { year: yearSel.dataset.placeholder || 'Year', make: makeSel.dataset.placeholder || 'Make', model: modelSel.dataset.placeholder || 'Model', trim: trimSel ? (trimSel.dataset.placeholder || 'Trim (optional)') : '' };
      function fill(sel, opts, placeholder, labelFn) {
        sel.innerHTML = '<option value="">' + esc(placeholder) + '</option>' + opts.map(function (o) { return '<option value="' + esc(o) + '">' + esc(labelFn ? labelFn(o) : o) + '</option>'; }).join('');
      }
      function ready() { var ok = yearSel.value && makeSel.value && modelSel.value; if (submit) submit.disabled = !ok; return ok; }
      loadIndex().then(function (idx) {
        if (!idx) return;
        fill(yearSel, idx.years.map(String), strings.year);
        function onYear() {
          fill(makeSel, idx.yearMakes[yearSel.value] || [], yearSel.value ? strings.make : (makeSel.dataset.locked || 'Select year first'), function (m) { return MAKE_DISPLAY[m] || m; });
          makeSel.disabled = !yearSel.value;
          fill(modelSel, [], modelSel.dataset.locked || 'Select make first'); modelSel.disabled = true;
          if (trimSel) { fill(trimSel, [], strings.trim); trimSel.disabled = true; }
          if (trimStep) trimStep.hidden = true;
          var def = (vs.dataset.vsDefaultMake || '').toLowerCase();
          if (def && yearSel.value) { var hit = Array.prototype.slice.call(makeSel.options).filter(function (o) { return o.value && def.indexOf(o.value.toLowerCase()) !== -1; })[0]; if (hit) { makeSel.value = hit.value; onMake(); } }
          ready();
        }
        function onMake() {
          var models = ((idx.yearMakeModels[yearSel.value] || {})[makeSel.value]) || [];
          fill(modelSel, models, makeSel.value ? strings.model : (modelSel.dataset.locked || 'Select make first'));
          modelSel.disabled = !makeSel.value;
          if (trimSel) { fill(trimSel, [], strings.trim); trimSel.disabled = true; }
          if (trimStep) trimStep.hidden = true;
          ready();
        }
        function onModel() {
          if (trimSel) {
            var trims = ((idx.modelTrims || {})[makeSel.value] || {})[modelSel.value] || [];
            var show = S.showTrim !== false && trims.length > 0;
            if (trimStep) trimStep.hidden = !show;
            fill(trimSel, trims, strings.trim); trimSel.disabled = !show;
          }
          ready();
        }
        yearSel.addEventListener('change', onYear);
        makeSel.addEventListener('change', onMake);
        modelSel.addEventListener('change', onModel);
        yearSel.disabled = false;
        function has(sel, val) {
          if (!val) return false;
          return Array.prototype.some.call(sel.options, function (o) { return o.value && o.value.toLowerCase() === String(val).toLowerCase(); });
        }
        function pick(sel, val) {
          var hit = Array.prototype.filter.call(sel.options, function (o) { return o.value && o.value.toLowerCase() === String(val).toLowerCase(); })[0];
          if (hit) { sel.value = hit.value; return true; }
          return false;
        }
        // Seeds come from a decoded search query. Carry whatever we understood
        // straight into the dropdowns so nobody retypes what they already said.
        var seed = { year: vs.dataset.vsSeedYear || '', make: vs.dataset.vsSeedMake || '', model: vs.dataset.vsSeedModel || '', trim: vs.dataset.vsSeedTrim || '' };
        var v = state.vehicle;
        if (seed.year || seed.make || seed.model) {
          if (has(yearSel, seed.year)) { pick(yearSel, seed.year); onYear(); } else { onYear(); }
          if (has(makeSel, seed.make)) { pick(makeSel, seed.make); onMake(); }
          if (has(modelSel, seed.model)) { pick(modelSel, seed.model); onModel(); }
          if (trimSel && has(trimSel, seed.trim)) pick(trimSel, seed.trim);
          ready();
        } else if (v && vs.dataset.vsPrefill !== 'off') {
          // Prefill from the saved vehicle so "Change" opens on the current choice.
          yearSel.value = v.year; onYear();
          if (v.make) { makeSel.value = v.make; onMake(); }
          if (v.model) { modelSel.value = v.model; onModel(); }
          if (v.trim && trimSel) trimSel.value = v.trim;
          ready();
        } else { onYear(); }
      });
      if (submit) submit.addEventListener('click', function (e) {
        e.preventDefault();
        if (!ready()) return;
        var v = { year: yearSel.value, make: makeSel.value, model: modelSel.value, trim: trimSel ? trimSel.value : '' };
        var action = vs.dataset.vsAction || 'search';
        if (action === 'form') {
          var form = vs.closest('form'); var inp = form && form.querySelector('[data-vehicle-field]');
          if (inp) inp.value = vehicleLabel(v); saveVehicle(v); applyVehicle();
          var hint = $('[data-vs-hint]', vs); if (hint) hint.textContent = 'Using ' + vehicleLabel(v);
          return;
        }
        saveVehicle(v);
        if (action === 'sheet') { closeSheet(); applyVehicle(); return; }
        if (action === 'save') { applyVehicle(); return; }
        var kw = vs.dataset.vsKeyword || '';
        var q = [v.year, v.make, v.model, kw].filter(Boolean).join(' ');
        window.location.href = (R.search || '/search') + '?type=product&q=' + encodeURIComponent(q);
      });
    });
  }

  function chipHTML(v, opts) {
    opts = opts || {};
    return '<span class="bbp-chip' + (opts.tone === 'dark' ? ' bbp-chip--dark' : (opts.active === false ? '' : ' bbp-chip--active')) + (opts.size === 'sm' ? ' bbp-chip--sm' : '') + '">' +
      icon('car-front', 14) + '<span class="bbp-chip__label">' + esc(vehicleLabel(v)) + '</span>' +
      (opts.change === false ? '' : '<button type="button" class="bbp-chip__change" data-open-vehicle>Change</button>') +
      (opts.remove ? '<button type="button" class="bbp-chip__remove" data-clear-vehicle aria-label="Remove vehicle">' + icon('x', 13) + '</button>' : '') +
      '</span>';
  }
  function renderChips() {
    var v = state.vehicle;
    $$('[data-vehicle-chip]').forEach(function (host) {
      var tone = host.dataset.tone || 'light';
      host.innerHTML = v ? chipHTML(v, { tone: tone, size: host.dataset.size, remove: host.dataset.remove === '1', change: host.dataset.change !== '0', active: host.dataset.active !== '0' }) : '';
      host.hidden = !v;
    });
    $$('[data-add-vehicle]').forEach(function (b) { b.hidden = !!v; });
    $$('[data-vehicle-strip]').forEach(function (strip) {
      strip.innerHTML = (v ? chipHTML(v, { tone: 'dark', size: 'sm', change: false }) : '<span class="bbp-vstrip__add">' + icon('car-front', 15) + ' Add your vehicle</span>') + icon('chevron-right', 15);
    });
    $$('[data-vehicle-label]').forEach(function (el) { el.textContent = v ? vehicleLabel(v) : ''; });
  }
  function renderBanners(idx) {
    var v = state.vehicle;
    $$('[data-fit-banner]').forEach(function (b) {
      var status = 'unknown';
      var note = b.dataset.noteUnknown || '';
      if (v) {
        var ctx = b.dataset.fitContext || 'listing';
        if (ctx === 'product') { status = fitStatus(idx, b.dataset.fitHandle); }
        else status = 'fits';
        note = (status === 'fits' ? b.dataset.noteFits : status === 'nofit' ? b.dataset.noteNofit : b.dataset.noteUnknown) || '';
        note = note.replace('%vehicle%', vehicleLabel(v));
      }
      b.className = b.className.replace(/\bbbp-fit--(fits|nofit|unknown)\b/g, '').trim() + ' bbp-fit--' + status;
      var head = $('[data-fit-head]', b); if (head) head.textContent = status === 'fits' ? (b.dataset.headFits || 'Fits your vehicle') : status === 'nofit' ? (b.dataset.headNofit || 'Does not fit your vehicle') : (b.dataset.headUnknown || 'Add your vehicle to check fitment');
      var noteEl = $('[data-fit-note]', b); if (noteEl) { noteEl.textContent = note; noteEl.hidden = !note; }
      var chip = $('[data-fit-chip]', b); if (chip) { chip.innerHTML = v ? chipHTML(v, { active: status === 'fits' }) : ''; chip.hidden = !v; }
      var sel = $('[data-fit-select]', b); if (sel) sel.hidden = !!v;
    });
  }
  function paintFitment(idx, root) {
    $$('[data-bbp-card]', root).forEach(function (card) {
      var st = fitStatus(idx, card.dataset.handle);
      card.classList.remove('is-fits', 'is-nofit'); if (st !== 'unknown') card.classList.add('is-' + st);
      var slot = $('[data-fit-badge]', card);
      if (slot) slot.innerHTML = st === 'fits' ? '<span class="bbp-badge bbp-badge--fits bbp-badge--sm">' + icon('circle-check', 11) + 'Fits your vehicle</span>' : st === 'nofit' ? '<span class="bbp-badge bbp-badge--nofit bbp-badge--sm">' + icon('circle-x', 11) + 'Does not fit</span>' : '';
    });
  }
  function renderGarage() {
    $$('[data-garage-list]').forEach(function (list) {
      if (!state.garage.length) { list.innerHTML = '<div class="bbp-garage__empty">No saved vehicles yet.</div>'; return; }
      var cur = state.vehicle ? vehicleId(state.vehicle) : '';
      list.innerHTML = state.garage.map(function (g) {
        var id = vehicleId(g), on = id === cur;
        return '<div class="bbp-garage__row' + (on ? ' is-active' : '') + '">' + icon('car-front', 15) + '<span class="bbp-garage__name">' + esc(vehicleLabel(g)) + '</span>' +
          (on ? '<span class="bbp-badge bbp-badge--fits bbp-badge--sm">Active</span>' : '<button type="button" class="bbp-btn bbp-btn--outline bbp-btn--sm" data-garage-use="' + esc(id) + '">Use</button>') +
          '<button type="button" class="bbp-iconbtn bbp-iconbtn--ghost bbp-iconbtn--sm" data-garage-delete="' + esc(id) + '" aria-label="Delete">' + icon('x', 15) + '</button></div>';
      }).join('');
    });
  }
  function applyVehicle() {
    renderChips(); renderGarage();
    loadIndex().then(function (idx) {
      renderBanners(idx); paintFitment(idx, document);
      if (listing) listing.onVehicleChange();
      renderFitsRail(idx);
      document.dispatchEvent(new CustomEvent('bbp:vehicle', { detail: state.vehicle }));
    });
  }
  // A search that names a whole vehicle should leave the site knowing that
  // vehicle, so the picker, banners and every later page are already set to it.
  // Same as applyVehicle but without re-entering the listing, which on a search
  // page would restart the very search that called this.
  function adoptVehicleFromSearch(v) {
    if (!v || !v.year || !v.make || !v.model) return;
    var cur = state.vehicle;
    if (cur && vehicleId(cur) === vehicleId(v)) return;
    saveVehicle(v);
    renderChips(); renderGarage();
    loadIndex().then(function (idx) {
      renderBanners(idx); paintFitment(idx, document); renderFitsRail(idx);
      document.dispatchEvent(new CustomEvent('bbp:vehicle', { detail: state.vehicle }));
    });
  }

  /* =====================================================================
     4. Product renderers (client side)
     ===================================================================== */
  var _prodCache = {};
  function normalizeProduct(p) {
    // Accepts a /products/{handle}.js object or a /collections/{h}/products.json item.
    if (!p) return null;
    var v = (p.variants && p.variants[0]) || {};
    var priceCents = typeof p.price === 'number' ? p.price : Math.round(parseFloat(v.price || 0) * 100);
    var cmp = typeof p.compare_at_price === 'number' ? p.compare_at_price : (v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : 0);
    var img = p.featured_image || (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
    if (img && img.indexOf('//') === 0) img = 'https:' + img;
    var tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
    var available = typeof p.available === 'boolean' ? p.available : (p.variants || []).some(function (x) { return x.available; });
    return { handle: p.handle, title: p.title, tags: tags, type: p.product_type || p.type || '', url: '/products/' + p.handle, image: img, available: available, price: priceCents, compareAt: cmp, sku: v.sku || '', variantId: v.id, freight: isFreight(tags), parsed: parseTitle(p.title, tags, p.product_type || p.type) };
  }
  function fetchProduct(h) {
    if (_prodCache[h]) return _prodCache[h];
    _prodCache[h] = fetch('/products/' + encodeURIComponent(h) + '.js').then(function (r) { return r.ok ? r.json() : null; }).then(normalizeProduct).catch(function () { return null; });
    return _prodCache[h];
  }
  function imgUrl(src, w) { if (!src) return ''; return src.replace(/(\.[a-z]{3,4})(\?|$)/i, '_' + w + 'x$1$2'); }
  function sideBadge(side, sm) { if (!side) return ''; var t = side === 'L' ? 'left' : side === 'R' ? 'right' : 'pair'; var l = side === 'L' ? 'LH driver' : side === 'R' ? 'RH passenger' : 'Pair'; return '<span class="bbp-badge bbp-badge--' + t + (sm ? ' bbp-badge--sm' : '') + '">' + l + '</span>'; }
  function titleHTML(p, lines, size, tag) {
    var t = p.parsed; tag = tag || 'h3';
    return '<div class="bbp-ptitle' + (size === 'lg' ? ' bbp-ptitle--lg' : '') + '" data-lines="' + (lines || 2) + '">' +
      '<div class="bbp-ptitle__meta">' + (t.years.length ? '<span class="bbp-years"><span class="bbp-years__chip">' + esc(t.yearsLabel) + '</span></span>' : '') + sideBadge(t.side, true) + (t.finish ? '<span class="bbp-badge bbp-badge--sm">' + esc(t.finish) + '</span>' : '') + '</div>' +
      '<' + tag + ' class="bbp-ptitle__name"><a href="' + esc(p.url) + '">' + esc(t.part) + '</a></' + tag + '>' +
      (t.vehicles.length ? '<span class="bbp-ptitle__vehicles">' + esc(t.vehicles.slice(0, 3).join(' · ') + (t.vehicles.length > 3 ? ' · +' + (t.vehicles.length - 3) + ' more' : '')) + '</span>' : '') +
      '</div>';
  }
  function photoHTML(p, cls, w) {
    return '<div class="bbp-photo-well bbp-photo ' + (cls || '') + '">' + (p.image ? '<img src="' + esc(imgUrl(p.image, w || 600)) + '" alt="' + esc(p.title) + '" loading="lazy">' : '<span class="bbp-photo__empty">No image yet</span>') +
      (p.parsed.side ? '<span class="bbp-photo__side">' + sideBadge(p.parsed.side, true) + '</span>' : '') + '</div>';
  }
  function priceHTML(p, lg) {
    var on = p.compareAt && p.compareAt > p.price;
    return '<span class="bbp-price' + (lg ? ' bbp-price--lg' : '') + '">' + esc(money(p.price)) + (on ? '<span class="bbp-price__was">' + esc(money(p.compareAt)) + '</span>' : '') + '</span>';
  }
  function stockBadge(p) { return p.available ? '<span class="bbp-badge bbp-badge--instock bbp-badge--sm">' + icon('check', 11) + 'In stock</span>' : '<span class="bbp-badge bbp-badge--outstock bbp-badge--sm">' + icon('minus', 11) + 'Out of stock</span>'; }
  function addFormHTML(p, cls, label) {
    if (!p.available) return '<button type="button" class="bbp-btn bbp-btn--outline ' + (cls || '') + '" disabled>' + icon('bell', 15) + 'Notify me</button>';
    return '<form class="bbp-card__form" action="' + esc(R.cartAdd || '/cart/add') + '" method="post" data-quick-add><input type="hidden" name="id" value="' + esc(p.variantId) + '"><input type="hidden" name="quantity" value="1" data-qty-value>' + (p.freight ? '<input type="hidden" name="properties[_ship]" value="freight">' : '') +
      '<button type="submit" class="bbp-btn ' + (cls || '') + '">' + icon('shopping-cart', 15) + (label || 'Add to cart') + '</button></form>';
  }
  function cardHTML(p) {
    return '<article class="bbp-card" data-bbp-card data-handle="' + esc(p.handle) + '" data-freight="' + (p.freight ? 1 : 0) + '">' +
      '<a class="bbp-card__media" href="' + esc(p.url) + '" aria-label="' + esc(p.title) + '">' + photoHTML(p, '', 600) + '</a>' +
      '<div class="bbp-card__body"><span class="bbp-card__fit" data-fit-badge></span>' + titleHTML(p, 2) +
      '<div class="bbp-card__ids"><span class="bbp-card__sku">' + esc(p.sku) + '</span>' + (S.brandLabel ? '<span class="bbp-card__brand">' + esc(S.brandLabel) + '</span>' : '') + '</div>' +
      '<div class="bbp-card__price">' + priceHTML(p) + '<span class="bbp-card__badges">' + stockBadge(p) + (p.freight ? '<span class="bbp-badge bbp-badge--freight bbp-badge--sm">' + icon('truck', 11) + 'By truck</span>' : '') + '</span></div>' +
      addFormHTML(p, 'bbp-btn--full') + '</div></article>';
  }
  function rowHTML(p) {
    return '<div class="bbp-row" data-bbp-card data-handle="' + esc(p.handle) + '" data-freight="' + (p.freight ? 1 : 0) + '">' +
      '<a href="' + esc(p.url) + '" class="bbp-row__media" aria-label="' + esc(p.title) + '">' + photoHTML(p, 'bbp-photo--sq', 200) + '</a>' +
      '<div class="bbp-row__col"><span class="bbp-card__fit" data-fit-badge></span>' + titleHTML(p, 1) + '</div>' +
      '<div class="bbp-row__col"><span class="bbp-data">' + esc(p.sku) + '</span><span class="bbp-card__brand">' + esc(S.brandLabel || '') + '</span></div>' +
      '<div class="bbp-row__col">' + stockBadge(p) + (p.freight ? '<span class="bbp-badge bbp-badge--freight bbp-badge--sm">' + icon('truck', 11) + 'By truck</span>' : '') + '</div>' +
      '<div class="bbp-row__buy"><span class="bbp-row__price">' + esc(money(p.price)) + '</span>' + (p.available ? '<form action="' + esc(R.cartAdd || '/cart/add') + '" method="post" data-quick-add class="bbp-row__form"><input type="hidden" name="id" value="' + esc(p.variantId) + '">' + (p.freight ? '<input type="hidden" name="properties[_ship]" value="freight">' : '') + '<input class="bbp-row__qty" type="number" name="quantity" min="1" value="1" aria-label="Quantity"><button type="submit" class="bbp-btn bbp-btn--sm" aria-label="Add to cart">' + icon('plus', 15) + '</button></form>' : '<button type="button" class="bbp-btn bbp-btn--outline bbp-btn--sm" disabled>Notify me</button>') + '</div></div>';
  }

  /* =====================================================================
     5. Listing engine (collection client mode + fitment-aware search)
     ===================================================================== */
  var MAKE_ALIASES = { infiniti: 'Infiniti', infinity: 'Infiniti', infinit: 'Infiniti', infinti: 'Infiniti', chevy: 'Chevrolet', chev: 'Chevrolet', chevrolet: 'Chevrolet', mercedes: 'Mercedes-Benz', benz: 'Mercedes-Benz', 'mercedes-benz': 'Mercedes-Benz', merc: 'Mercedes-Benz', toyata: 'Toyota', toyota: 'Toyota', mistubishi: 'Mitsubishi', mitsibishi: 'Mitsubishi', mitsubishi: 'Mitsubishi', ram: 'Dodge', dodge: 'Dodge', gmc: 'GMC', nissan: 'Nissan', honda: 'Honda', ford: 'Ford', lexus: 'Lexus', freightliner: 'Freightliner', sprinter: 'Mercedes-Benz', promaster: 'Dodge' };
  var MAKE_TOKENS = (function () { var s = {}; Object.keys(MAKE_ALIASES).forEach(function (k) { s[k] = 1; }); ['jeep', 'genesis', 'bmw', 'acura', 'subaru', 'mazda', 'hyundai', 'kia', 'cadillac', 'buick', 'lincoln', 'chrysler', 'volkswagen', 'audi', 'volvo'].forEach(function (m) { s[m] = 1; }); return s; })();
  var PART_SYNONYMS = { bumper: ['fascia'], fascia: ['bumper'], headlamp: ['headlight'], taillight: ['tail light'], taillamp: ['tail light'], rim: ['wheel'], foglight: ['fog light'], fog: ['fog light'], signal: ['signal'] };
  var PART_ORDER = ['Bumper Cover', 'Bumper Kit', 'Grille', 'Grille Trim', 'Bumper Bracket', 'Bumper Trim & Molding', 'Bumper Valance', 'Bumper End Cap', 'Fog Light Cover', 'Signal & Fog Light', 'Headlight', 'Headlight Trim', 'Tail Light', 'Fender', 'Fender Flare', 'Fender Liner', 'Fender Molding', 'Mirror', 'Radiator Support', 'Radiator Support Kit', 'Door Molding & Trim', 'Door Shell Panel', 'Body Side Molding', 'Trunk Lid & Tailgate', 'Hood', 'Air Intake'];
  var STOPWORDS = { 'for': 1, 'the': 1, 'and': 1, 'with': 1, 'oem': 1, 'new': 1, 'part': 1, 'parts': 1, 'auto': 1, 'car': 1, 'a': 1, 'of': 1, 'my': 1, 'van': 1 };
  function lev(a, b) { a = a || ''; b = b || ''; var m = a.length, n = b.length; if (!m) return n; if (!n) return m; var prev = [], cur = [], i, j; for (j = 0; j <= n; j++) prev[j] = j; for (i = 1; i <= m; i++) { cur[0] = i; for (j = 1; j <= n; j++) { var c = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1; cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + c); } for (j = 0; j <= n; j++) prev[j] = cur[j]; } return prev[n]; }
  function resolveMake(token, makes) { if (!token) return null; var tl = token.toLowerCase(); if (MAKE_ALIASES[tl]) return MAKE_ALIASES[tl]; var i; for (i = 0; i < makes.length; i++) if (makes[i].toLowerCase() === tl) return makes[i]; for (i = 0; i < makes.length; i++) { var ml = makes[i].toLowerCase(); if (ml.length >= 5 && lev(tl, ml) <= 1) return makes[i]; } return null; }
  function queryHasMake(q) { return String(q || '').toLowerCase().split(/[^a-z0-9-]+/).some(function (t) { return MAKE_TOKENS[t]; }); }
  function parseQuery(q, idx) {
    var tokens = String(q || '').toLowerCase().split(/[^a-z0-9-]+/).filter(Boolean);
    var years = [], make = null, model = null, trim = '', kws = [], used = {}, i;
    var yearSet = {}; (idx.years || []).forEach(function (y) { yearSet[String(y)] = 1; });
    tokens.forEach(function (tk, ix) { if (/^(19|20)\d\d$/.test(tk) && yearSet[tk]) { years.push(tk); used[ix] = 1; } else if (/^\d{2}$/.test(tk)) { var yy = parseInt(tk, 10); var full = String(yy <= 49 ? 2000 + yy : 1900 + yy); if (yearSet[full]) { years.push(full); used[ix] = 1; } } });
    for (i = 0; i < tokens.length; i++) { if (used[i]) continue; var mk = resolveMake(tokens[i], idx.makes || []); if (mk) { make = mk; used[i] = 1; break; } }
    if (!make) {
      var rev = {}; var mm0 = idx.makeModels || {};
      Object.keys(mm0).forEach(function (mk2) { (mm0[mk2] || []).forEach(function (m0) { var fw = String(m0).toLowerCase().split(/\s+/)[0]; if (fw && fw.length >= 2 && !/^\d+$/.test(fw)) (rev[fw] = rev[fw] || {})[mk2] = 1; }); });
      for (i = 0; i < tokens.length; i++) { if (used[i]) continue; var cand = rev[tokens[i]]; if (cand) { var ks = Object.keys(cand); if (ks.length === 1) { make = ks[0]; break; } } }
    }
    if (make) {
      var models = (idx.makeModels && idx.makeModels[make]) ? idx.makeModels[make] : [];
      var byLen = models.slice().sort(function (a, b) { return b.length - a.length; });
      outer: for (var mi = 0; mi < byLen.length; mi++) {
        var mwords = byLen[mi].toLowerCase().split(/\s+/);
        for (i = 0; i + mwords.length <= tokens.length; i++) { var ok = true; for (var w = 0; w < mwords.length; w++) { if (used[i + w] || tokens[i + w] !== mwords[w]) { ok = false; break; } } if (ok) { model = byLen[mi]; for (w = 0; w < mwords.length; w++) used[i + w] = 1; break outer; } }
      }
      if (!model) {
        var mlow = models.map(function (m) { return m.toLowerCase(); });
        for (i = 0; i < tokens.length; i++) { if (used[i]) continue; var tl = tokens[i]; var wm = []; for (var j = 0; j < mlow.length; j++) if (mlow[j].split(/\s+/).indexOf(tl) !== -1) wm.push(j); if (wm.length === 1) { model = models[wm[0]]; used[i] = 1; break; } }
      }
      if (model && idx.modelTrims && idx.modelTrims[make] && idx.modelTrims[make][model]) {
        var trims = idx.modelTrims[make][model];
        for (i = 0; i < tokens.length; i++) { if (used[i]) continue; var hit = trims.filter(function (t) { return t.toLowerCase() === tokens[i]; })[0]; if (hit) { trim = hit; used[i] = 1; break; } }
      }
    }
    for (i = 0; i < tokens.length; i++) { if (used[i]) continue; var t2 = tokens[i]; if (t2.length < 2 || STOPWORDS[t2]) continue; kws.push(t2); }
    return { years: years, make: make, model: model, trim: trim, keywords: kws, raw: q };
  }
  function metaMatches(m, kws) {
    if (!kws.length) return true;
    var hay = (m.t + ' ' + m.ty + ' ' + m.pp).toLowerCase();
    return kws.every(function (k) { if (hay.indexOf(k) !== -1) return true; var syn = PART_SYNONYMS[k]; if (syn) for (var s = 0; s < syn.length; s++) if (hay.indexOf(syn[s]) !== -1) return true; return false; });
  }
  function vehicleHandles(idx, parsed) {
    var yrs = parsed.years.length ? parsed.years : Object.keys(idx.fitment || {});
    var set = {};
    yrs.forEach(function (y) { var mm = (idx.fitment[y] || {})[parsed.make]; if (!mm) return; Object.keys(mm).forEach(function (mdl) { if (parsed.model && mdl.toLowerCase() !== parsed.model.toLowerCase()) return; mm[mdl].forEach(function (h) { set[h] = 1; }); }); });
    var hs = Object.keys(set);
    if (parsed.trim && idx.handleTrims) { var t = parsed.trim.toUpperCase(); hs = hs.filter(function (h) { var ht = idx.handleTrims[h]; return !ht || ht.some(function (x) { return String(x).toUpperCase() === t; }); }); }
    return hs;
  }

  var listing = null;
  function Listing(host) {
    this.host = host;
    this.type = host.dataset.listingType || 'collection';
    this.handle = host.dataset.collectionHandle || '';
    this.query = host.dataset.query || '';
    this.native = $('[data-native]', host);
    this.client = $('[data-client]', host);
    this.countEl = $('[data-listing-count]', host);
    this.titleEl = $('[data-listing-title]', host);
    this.fitOnly = $('[data-fit-only]', host);
    this.sortEl = $('[data-sort]', host);
    this.chipsEl = $('[data-chips]', host);
    this.facetHost = $('[data-cfacets]', host);
    this.products = null; this.loading = null;
    this.filters = {}; this.sort = 'best';
    this.mode = 'native';
    this.baseTitle = this.titleEl ? this.titleEl.textContent.trim() : '';
    this.init();
  }
  Listing.prototype.init = function () {
    var self = this;
    if (this.fitOnly) {
      this.fitOnly.checked = !!state.vehicle && S.defaultOnlyFit !== false;
      this.fitOnly.addEventListener('change', function () { self.refresh(); });
      var wrap = this.fitOnly.closest('[data-fit-only-wrap]'); if (wrap) wrap.hidden = !state.vehicle;
    }
    if (this.sortEl) this.sortEl.addEventListener('change', function () { if (self.sortEl.dataset.native === '1' && self.mode === 'native') return; self.sort = self.sortEl.value; self.refresh(); });
    if (this.facetHost) this.facetHost.addEventListener('change', function (e) { var cb = e.target; if (!cb.matches('[data-cfacet]')) return; var g = cb.dataset.cfacet, v = cb.value; self.filters[g] = self.filters[g] || []; var ix = self.filters[g].indexOf(v); if (cb.checked && ix === -1) self.filters[g].push(v); if (!cb.checked && ix !== -1) self.filters[g].splice(ix, 1); self.refresh(); });
    host_clear: {
      var clr = $('[data-clear-filters]', this.host);
      if (clr) clr.addEventListener('click', function (e) { e.preventDefault(); self.filters = {}; $$('[data-cfacet]', self.host).forEach(function (c) { c.checked = false; }); self.refresh(); });
    }
    if (this.type === 'search') this.runSearch(); else this.refresh();
  };
  Listing.prototype.onVehicleChange = function () {
    this._facetsPrimed = false;   // counts are vehicle-dependent, rebuild them
    if (this.fitOnly) { this.fitOnly.checked = !!state.vehicle && S.defaultOnlyFit !== false; var wrap = this.fitOnly.closest('[data-fit-only-wrap]'); if (wrap) wrap.hidden = !state.vehicle; }
    if (this.type === 'search') this.runSearch(); else this.refresh();
  };
  Listing.prototype.clientActive = function () {
    var anyFacet = Object.keys(this.filters).some(function (k) { return this.filters[k] && this.filters[k].length; }, this);
    return (this.fitOnly && this.fitOnly.checked && state.vehicle) || anyFacet || (this.sort !== 'best' && this.sortEl && this.sortEl.dataset.native !== '1');
  };
  Listing.prototype.loadCollection = function () {
    var self = this;
    if (this.products) return Promise.resolve(this.products);
    if (this.loading) return this.loading;
    var all = [];
    function page(p) {
      return fetch(R.collections + '/' + encodeURIComponent(self.handle) + '/products.json?limit=250&page=' + p).then(function (r) { return r.ok ? r.json() : null; }).then(function (d) {
        if (!d || !d.products || !d.products.length) return all;
        d.products.forEach(function (pr) { var n = normalizeProduct(pr); if (n) { all.push(n); _prodCache[n.handle] = Promise.resolve(n); } });
        return d.products.length === 250 && p < 12 ? page(p + 1) : all;
      }).catch(function () { return all; });
    }
    this.loading = page(1).then(function (list) { self.products = list; return list; });
    return this.loading;
  };
  Listing.prototype.refresh = function () {
    var self = this;
    if (this.type === 'search') { this.paintSearch(); return; }
    if (!this.clientActive()) { this.showNative(); return; }
    this.client.hidden = false; if (this.native) this.native.hidden = true;
    if (!this.products) this.client.innerHTML = '<div class="bbp-loading">Loading every part in this category</div>';
    Promise.all([this.loadCollection(), loadIndex()]).then(function (res) { self.paint(res[0], res[1], { total: res[0].length }); });
  };
  Listing.prototype.showNative = function () {
    var self = this;
    this.mode = 'native';
    if (this.native) this.native.hidden = false; if (this.client) { this.client.hidden = true; this.client.innerHTML = ''; }
    if (this.countEl && this.countEl.dataset.nativeCount) this.countEl.textContent = this.countEl.dataset.nativeCount;
    if (this.titleEl) this.titleEl.textContent = this.baseTitle;
    if (this.facetHost) this.facetHost.hidden = false;
    loadIndex().then(function (idx) { paintFitment(idx, document); });
    // Fill the filter panel even while Shopify's own grid is showing. Without this
    // the panel stays empty until the shopper has already filtered, which they
    // cannot do because there is nothing to click.
    if (this.facetHost && !this._facetsPrimed) {
      this._facetsPrimed = true;
      Promise.all([this.primePool(), loadIndex()]).then(function (res) {
        if (self.mode !== 'native') return;
        self.renderFacets(self.facetCounts(res[0], res[1]));
      }).catch(function () { self._facetsPrimed = false; });
    }
  };
  function facetValue(p, g) {
    var t = p.parsed;
    if (g === 'side') return t.side || '';
    if (g === 'pos') { var l = t.part.toLowerCase(); return /\bfront\b/.test(l) ? 'front' : /\brear\b/.test(l) ? 'rear' : ''; }
    if (g === 'finish') return (t.finish || '').toLowerCase().replace(/\s+/g, '-');
    if (g === 'ship') return p.freight ? 'freight' : 'parcel';
    if (g === 'avail') return p.available ? 'instock' : 'outstock';
    if (g === 'cat') return p.type || '';
    return '';
  }
  // Counts for the filter panel. Shared by both modes so the numbers in the
  // panel always match what clicking a filter would actually return.
  // Pool used purely to count filters. On the all-parts page that is every product,
  // and search-meta.json already carries what the facets need, so use it instead of
  // paging products.json eight times for ~560KB we would otherwise throw away.
  Listing.prototype.primePool = function () {
    var self = this;
    if (this.handle !== 'all') return this.loadCollection();
    return loadMeta().then(function (meta) {
      if (!meta) return self.loadCollection();
      return Object.keys(meta).map(function (h) {
        var m = meta[h];
        return { handle: h, parsed: parseTitle(m.t, [], m.ty), type: m.pp || m.ty, freight: !!m.fr, available: !!m.av };
      });
    }).catch(function () { return self.loadCollection(); });
  };
  Listing.prototype.facetCounts = function (products, idx) {
    var v = state.vehicle;
    var fitOn = this.fitOnly ? (this.fitOnly.checked && v) : false;
    var fs = (v && idx) ? fitSetFor(idx, v) : null;
    var pool = products;
    if (fitOn && fs) pool = products.filter(function (p) { return fs.has(p.handle); });
    var groups = ['cat', 'side', 'pos', 'finish', 'avail', 'ship'];
    var counts = {}; groups.forEach(function (g) { counts[g] = {}; });
    pool.forEach(function (p) { groups.forEach(function (g) { var val = facetValue(p, g); if (val) counts[g][val] = (counts[g][val] || 0) + 1; }); });
    return counts;
  };
  Listing.prototype.paint = function (products, idx, opts) {
    var self = this; opts = opts || {};
    this.mode = 'client';
    var v = state.vehicle;
    var fitOn = this.fitOnly ? (this.fitOnly.checked && v) : false;
    var fs = (v && idx) ? fitSetFor(idx, v) : null;
    var pool0 = products;
    if (fitOn && fs) pool0 = products.filter(function (p) { return fs.has(p.handle); });
    // Facet counts come from the vehicle-filtered pool, so a count is the number you get if you click it.
    var groups = ['cat', 'side', 'pos', 'finish', 'avail', 'ship'];
    var counts = this.facetCounts(products, idx);
    var list = pool0.filter(function (p) { return groups.every(function (g) { var sel = self.filters[g]; if (!sel || !sel.length) return true; return sel.indexOf(facetValue(p, g)) !== -1; }); });
    if (this.sort === 'price-asc') list.sort(function (a, b) { return a.price - b.price; });
    else if (this.sort === 'price-desc') list.sort(function (a, b) { return b.price - a.price; });
    else if (this.sort === 'az') list.sort(function (a, b) { return a.parsed.part.localeCompare(b.parsed.part); });
    else if (opts.keywordOrder) { /* already ordered */ }
    else list.sort(function (a, b) { var fa = fs ? (fs.has(a.handle) ? 0 : 1) : 0, fb = fs ? (fs.has(b.handle) ? 0 : 1) : 0; if (fa !== fb) return fa - fb; if (a.available !== b.available) return a.available ? -1 : 1; var oa = PART_ORDER.indexOf(a.type), ob = PART_ORDER.indexOf(b.type); if (oa < 0) oa = 99; if (ob < 0) ob = 99; return oa - ob; });
    this.renderFacets(counts);
    var total = opts.total != null ? opts.total : products.length;
    if (this.countEl) this.countEl.textContent = list.length + ' of ' + total + ' results';
    if (this.titleEl) {
      if (opts.title) this.titleEl.textContent = opts.title;
      else if (v && fitOn) {
        // On a make collection the base title is already in the vehicle label
        // ("2019 Toyota C-HR" + "Toyota"), so say "parts" instead of repeating it.
        var vl = vehicleLabel(v, true);
        var bt = this.baseTitle;
        var redundant = vl.toLowerCase().indexOf(bt.toLowerCase()) !== -1;
        this.titleEl.textContent = redundant ? vl + ' parts' : vl + ' ' + bt.charAt(0).toLowerCase() + bt.slice(1);
      }
      else this.titleEl.textContent = this.baseTitle;
    }
    if (this.chipsEl) {
      var chips = [];
      if (v && fitOn) chips.push('<span class="bbp-badge bbp-badge--fits">' + icon('circle-check', 11) + esc(vehicleLabel(v, true)) + '</span>');
      groups.forEach(function (g) { (self.filters[g] || []).forEach(function (val) { chips.push('<button type="button" class="bbp-badge bbp-badge--signal" data-chip-remove="' + esc(g) + '" data-chip-value="' + esc(val) + '">' + esc(self.facetLabel(g, val)) + '<span>×</span></button>'); }); });
      this.chipsEl.innerHTML = chips.join('');
    }
    if (!list.length) {
      this.client.innerHTML = '<div class="bbp-empty"><h3>' + (fitOn && v ? 'No parts in this category fit your ' + esc(vehicleLabel(v, true)) : 'No parts match') + '</h3><p>' + (fitOn && v ? 'Untick "Only parts that fit my vehicle" to see everything here, or change the vehicle.' : 'Try removing a filter.') + '</p>' + (fitOn && v ? '<button type="button" class="bbp-btn bbp-btn--outline" data-open-vehicle>Change vehicle</button>' : '') + '</div>';
      return;
    }
    var capped = list.slice(0, 240);
    this.client.innerHTML = (state.view === 'list')
      ? '<div class="bbp-rows"><div class="bbp-rows__head"><span></span><span>Part</span><span>Part number</span><span>Availability</span><span style="text-align:right">Price / add</span></div>' + capped.map(rowHTML).join('') + '</div>'
      : '<div class="bbp-grid">' + capped.map(cardHTML).join('') + '</div>';
    if (list.length > capped.length) this.client.innerHTML += '<p class="bbp-loading">Showing the first ' + capped.length + '. Narrow the results with a filter to see the rest.</p>';
    paintFitment(idx, this.client);
  };
  Listing.prototype.facetLabel = function (g, val) {
    var L = { side: { L: 'LH driver', R: 'RH passenger', PAIR: 'Pair / both' }, pos: { front: 'Front', rear: 'Rear' }, ship: { freight: 'Ships by truck', parcel: 'Standard shipping' }, avail: { instock: 'In stock', outstock: 'Out of stock' } };
    if (L[g] && L[g][val]) return L[g][val];
    if (g === 'finish') return val.replace(/-/g, ' ').replace(/^\w/, function (c) { return c.toUpperCase(); });
    return val;
  };
  Listing.prototype.renderFacets = function (counts) {
    var self = this; if (!this.facetHost) return;
    this.facetHost.hidden = false;
    // Shopify's own Availability duplicates ours, and its Product type duplicates
    // Part category, but ours count against the vehicle-filtered pool and update
    // without a reload. Keep only Price from the native form. With JS off none of
    // this runs and the full native set still renders for crawlers.
    var nativeForm = $('[data-native-filters]', this.host);
    if (nativeForm) $$('details', nativeForm).forEach(function (d) { d.hidden = !$('.bbp-facet__price', d); });
    var defs = [['cat', 'Part category', true], ['side', 'Side', true], ['pos', 'Position', false], ['finish', 'Finish', false], ['avail', 'Availability', false], ['ship', 'Shipping', false]];
    var order = { side: ['L', 'R', 'PAIR'], pos: ['front', 'rear'], ship: ['parcel', 'freight'], avail: ['instock', 'outstock'] };
    this.facetHost.innerHTML = defs.map(function (d) {
      var g = d[0], vals = Object.keys(counts[g] || {});
      if (order[g]) vals = order[g].filter(function (x) { return counts[g][x]; }); else vals.sort(function (a, b) { return counts[g][b] - counts[g][a]; });
      if (g === 'cat' && vals.length < 2) return '';
      if (!vals.length) return '';
      var sel = self.filters[g] || [];
      var open = d[2] || sel.length;
      return '<details class="bbp-facet"' + (open ? ' open' : '') + '><summary><span>' + esc(d[1]) + (sel.length ? ' <span class="bbp-badge bbp-badge--signal bbp-badge--sm bbp-facet__count-badge">' + sel.length + '</span>' : '') + '</span>' + icon('chevron-down', 15) + '</summary><div class="bbp-facet__body">' +
        vals.slice(0, 12).map(function (val) { return '<label class="bbp-check"><input type="checkbox" data-cfacet="' + esc(g) + '" value="' + esc(val) + '"' + (sel.indexOf(val) !== -1 ? ' checked' : '') + '><span class="bbp-check__box"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="4"><path d="M20 6L9 17l-5-5"/></svg></span><span class="bbp-check__label">' + esc(self.facetLabel(g, val)) + '</span><span class="bbp-check__count">' + counts[g][val] + '</span></label>'; }).join('') + '</div></details>';
    }).join('');
  };
  // Search: vehicle-aware. A make in the query, or a saved vehicle plus a part word, produces
  // the exact-fit set; a part word with no vehicle at all produces the vehicle gate; anything
  // else (a part number, a random word) stays on Shopify's own results.
  Listing.prototype.runSearch = function () {
    var self = this, q = this.query;
    if (!q.trim()) return;
    var hadMake = queryHasMake(q);
    if (hadMake && this.native) this.native.hidden = true;
    Promise.all([loadIndex(), loadMeta()]).then(function (res) {
      var idx = res[0], meta = res[1];
      if (!idx || !meta) { if (self.native) self.native.hidden = false; return; }
      var parsed = parseQuery(q, idx);
      var v = state.vehicle;
      var partLike = parsed.keywords.some(function (k) { return /^[a-z][a-z-]{2,}$/.test(k); });
      if (!parsed.make && v && (partLike || !parsed.keywords.length)) {
        parsed = { years: [v.year], make: v.make, model: v.model, trim: v.trim, keywords: parsed.keywords, raw: q };
      }
      if (!parsed.make) {
        if (partLike) { self.renderGate(parsed); return; }
        self.showNative(); return;
      }
      var handles = vehicleHandles(idx, parsed);
      // We understood the vehicle, we just have nothing for it. Say so, rather
      // than showing a blank picker that invites the same search again.
      if (!handles.length) { self.renderGate(parsed, 'nostock'); return; }
      var kws = parsed.keywords;
      var matched = handles.filter(function (h) { var m = meta[h]; return m && metaMatches(m, kws); });
      var show = matched.length ? matched : handles;
      var viewKws = matched.length ? kws : [];
      var dmake = MAKE_DISPLAY[parsed.make] || parsed.make;
      var vlabel = (parsed.years.length ? parsed.years.join('/') + ' ' : '') + dmake + (parsed.model ? ' ' + parsed.model : '') + (parsed.trim ? ' ' + parsed.trim : '');
      // The query named one specific vehicle, so make it the site's vehicle.
      if (parsed.years.length === 1 && parsed.model) {
        adoptVehicleFromSearch({ year: String(parsed.years[0]), make: parsed.make, model: parsed.model, trim: parsed.trim || '' });
      }
      if (self.native) self.native.hidden = true;
      self.client.hidden = false;
      self.client.innerHTML = '<div class="bbp-loading">Loading ' + show.length + ' parts for ' + esc(vlabel) + '</div>';
      // Relevance: keyword-matched part types first, then the merchandising order, then in stock.
      show.sort(function (a, b) { var ma = meta[a], mb = meta[b]; var pa = viewKws.some(function (k) { return ma.pp.toLowerCase().indexOf(k) !== -1; }) ? 0 : 1, pb = viewKws.some(function (k) { return mb.pp.toLowerCase().indexOf(k) !== -1; }) ? 0 : 1; if (pa !== pb) return pa - pb; var oa = PART_ORDER.indexOf(ma.pp), ob = PART_ORDER.indexOf(mb.pp); if (oa < 0) oa = 99; if (ob < 0) ob = 99; if (oa !== ob) return oa - ob; if (ma.av !== mb.av) return ma.av ? -1 : 1; return 0; });
      pool(show, fetchProduct, 6).then(function (items) {
        var products = items.filter(Boolean);
        self.searchState = { products: products, title: products.length + ' parts fit ' + vlabel + (viewKws.length ? ' matching “' + viewKws.join(' ') + '”' : ''), vlabel: vlabel };
        // Vehicle-search results are already exact-fit; the toggle would be redundant.
        if (self.fitOnly) { var wrap = self.fitOnly.closest('[data-fit-only-wrap]'); if (wrap) wrap.hidden = true; }
        document.title = self.searchState.title + ' – ' + (S.shopName || '');
        self.paintSearch();
      });
    });
  };
  Listing.prototype.paintSearch = function () {
    if (!this.searchState) return;
    var self = this, st = this.searchState;
    loadIndex().then(function (idx) {
      var savedFitOnly = self.fitOnly; self.fitOnly = null;   // exact-fit set is the pool already
      self.paint(st.products, idx, { total: st.products.length, title: st.title, keywordOrder: true });
      self.fitOnly = savedFitOnly;
    });
  };
  // The gate carries forward everything the query decoded. Asking someone to
  // retype a vehicle they already typed is the fastest way to lose the sale, so
  // the dropdowns arrive pre-set and the copy says what we understood.
  Listing.prototype.renderGate = function (parsed, reason) {
    if (this.native) this.native.hidden = true;
    this.client.hidden = false;
    var partLabel = parsed.keywords.length ? parsed.keywords.join(' ') : 'parts';
    var year = (parsed.years && parsed.years[0]) ? String(parsed.years[0]) : '';
    var dmake = parsed.make ? (MAKE_DISPLAY[parsed.make] || parsed.make) : '';
    var vlabel = [year, dmake, parsed.model, parsed.trim].filter(Boolean).join(' ');

    var eyebrow, heading, body;
    if (reason === 'nostock' && vlabel) {
      eyebrow = 'Nothing for that vehicle yet';
      heading = 'We do not carry ' + esc(partLabel === 'parts' ? 'parts' : partLabel) + ' for a ' + esc(vlabel) + ' yet.';
      body = 'Your search is filled in below. Change the year, make or model to look at another vehicle, or call 877-813-2121 and we will try to source it.';
    } else if (vlabel) {
      eyebrow = 'Confirm your vehicle';
      heading = 'We read that as ' + esc(vlabel) + '. Finish the missing part and we will show only what fits.';
      body = 'We filled in what your search told us. Complete the rest so you never see the wrong part.';
    } else {
      eyebrow = 'Pick your vehicle';
      heading = 'Which vehicle? We show only the ' + esc(partLabel) + ' that fit.';
      body = 'Choose year, make and model so you never see the wrong part.';
    }

    this.client.innerHTML = '<div class="bbp-gate"><span class="bbp-eyebrow">' + eyebrow + '</span><h2>' + heading + '</h2><p>' + body + '</p>' +
      '<div class="bbp-vs bbp-vs--flat" data-vs data-vs-action="search" data-vs-prefill="off"' +
      ' data-vs-keyword="' + esc(parsed.keywords.join(' ')) + '"' +
      ' data-vs-default-make="' + esc(parsed.make || '') + '"' +
      ' data-vs-seed-year="' + esc(year) + '"' +
      ' data-vs-seed-make="' + esc(parsed.make || '') + '"' +
      ' data-vs-seed-model="' + esc(parsed.model || '') + '"' +
      ' data-vs-seed-trim="' + esc(parsed.trim || '') + '">' + selectorInnerHTML(false) + '</div></div>';
    var pageTitle = vlabel ? (reason === 'nostock' ? 'No parts for ' + vlabel : 'Confirm ' + vlabel) : 'Pick your vehicle';
    if (this.titleEl) this.titleEl.textContent = pageTitle;
    document.title = pageTitle + ' – ' + (S.shopName || '');
    initSelectors(this.client);
  };
  function selectorInnerHTML(stack) {
    function step(n, label, key, ph, disabled) { return '<div class="bbp-vs__step bbp-field"' + (key === 'trim' ? ' data-vs-step-trim hidden' : '') + '><span class="bbp-field__label">' + label + '</span><span class="bbp-vs__n">' + n + '</span><div class="bbp-select"><select data-vs-' + key + ' aria-label="' + label + '" data-placeholder="' + ph + '"' + (disabled ? ' disabled' : '') + '><option value="">' + ph + '</option></select></div></div>'; }
    return '<div class="bbp-vs__head">' + icon('car-front', 18) + '<span>Find parts that fit your vehicle</span></div><div class="bbp-vs__steps">' + step('01', 'Year', 'year', 'Year', false) + step('02', 'Make', 'make', 'Make', true) + step('03', 'Model', 'model', 'Model', true) + step('04', 'Trim', 'trim', 'Trim (optional)', true) + '<div class="bbp-vs__step bbp-vs__step--submit"><button type="button" class="bbp-btn bbp-vs__submit" data-vs-submit disabled>' + icon('search', 16) + 'Show parts that fit</button></div></div>';
  }
  function initListing() {
    var host = $('[data-listing]');
    if (!host) return;
    listing = new Listing(host);
    host.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-chip-remove]');
      if (chip) { var g = chip.dataset.chipRemove, val = chip.dataset.chipValue; listing.filters[g] = (listing.filters[g] || []).filter(function (x) { return x !== val; }); listing.refresh(); }
    });
    $$('[data-view]').forEach(function (b) {
      b.classList.toggle('bbp-iconbtn--solid', b.dataset.view === state.view);
      b.addEventListener('click', function () {
        state.view = b.dataset.view; Store.set('bbp_view', state.view);
        $$('[data-view]').forEach(function (x) { x.classList.toggle('bbp-iconbtn--solid', x.dataset.view === state.view); });
        applyView();
        if (listing.mode === 'client') listing.refresh();
      });
    });
    applyView();
    var openF = $('[data-open-facets]'), facets = $('[data-facets-panel]'), closeF = $('[data-close-facets]');
    if (openF && facets) openF.addEventListener('click', function () { facets.classList.add('is-open'); document.body.classList.add('bbp-lock'); });
    if (closeF && facets) closeF.addEventListener('click', function () { facets.classList.remove('is-open'); document.body.classList.remove('bbp-lock'); });
  }
  function applyView() {
    $$('[data-native-grid]').forEach(function (g) { g.hidden = state.view !== 'grid'; });
    $$('[data-native-rows]').forEach(function (g) { g.hidden = state.view !== 'list'; });
  }

  // Predictive search under every search input.
  function initPredictive() {
    $$('input[name="q"][data-predict]').forEach(function (input) {
      var wrap = input.closest('.bbp-search__wrap') || input.parentNode;
      if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
      var box = document.createElement('div'); box.className = 'bbp-predict'; box.hidden = true; wrap.appendChild(box);
      var run = debounce(function () { var q = input.value.trim(); if (q.length < 2) { box.hidden = true; return; } predict(q, box); }, 140);
      input.setAttribute('autocomplete', 'off');
      input.addEventListener('input', run);
      input.addEventListener('focus', function () { if (input.value.trim().length >= 2 && box.innerHTML) box.hidden = false; });
      document.addEventListener('click', function (e) { if (!box.contains(e.target) && e.target !== input) box.hidden = true; });
    });
  }
  function predict(q, box) {
    Promise.all([loadIndex(), loadMeta()]).then(function (res) {
      var idx = res[0], meta = res[1]; if (!idx || !meta) return;
      var parsed = parseQuery(q, idx);
      var v = state.vehicle;
      if (!parsed.make && v) parsed = { years: [v.year], make: v.make, model: v.model, trim: v.trim, keywords: parsed.keywords, raw: q };
      var pool0 = parsed.make ? vehicleHandles(idx, parsed) : Object.keys(meta);
      var hits = pool0.filter(function (h) { var m = meta[h]; return m && metaMatches(m, parsed.keywords); });
      var groups = {}; hits.forEach(function (h) { var pp = meta[h].pp; (groups[pp] = groups[pp] || []).push(h); });
      var names = Object.keys(groups).sort(function (a, b) { var pa = parsed.keywords.some(function (k) { return a.toLowerCase().indexOf(k) !== -1; }) ? 0 : 1, pb = parsed.keywords.some(function (k) { return b.toLowerCase().indexOf(k) !== -1; }) ? 0 : 1; if (pa !== pb) return pa - pb; var oa = PART_ORDER.indexOf(a), ob = PART_ORDER.indexOf(b); if (oa < 0) oa = 99; if (ob < 0) ob = 99; if (oa !== ob) return oa - ob; return groups[b].length - groups[a].length; });
      var url = (R.search || '/search') + '?type=product&q=' + encodeURIComponent(q);
      var chips = names.slice(0, 6).map(function (pp) { return '<a class="bbp-predict__chip" href="' + url + '">' + esc(pp) + ' <b>' + groups[pp].length + '</b></a>'; }).join('');
      var ordered = []; names.forEach(function (pp) { groups[pp].forEach(function (h) { ordered.push(h); }); });
      var top = ordered.slice(0, 6).map(function (h) { var m = meta[h]; var t = parseTitle(m.t, [], m.ty); return '<a class="bbp-predict__item" href="/products/' + encodeURIComponent(h) + '"><span class="bbp-predict__t">' + esc((t.yearsLabel ? t.yearsLabel + ' · ' : '') + t.part) + '</span><span class="bbp-predict__ty">' + esc(m.pp) + '</span></a>'; }).join('');
      var label = parsed.make ? ((parsed.years[0] ? parsed.years[0] + ' ' : '') + (MAKE_DISPLAY[parsed.make] || parsed.make) + (parsed.model ? ' ' + parsed.model : '')) : q;
      box.innerHTML = (chips ? '<div class="bbp-predict__chips">' + chips + '</div>' : '') + (top || '<div class="bbp-predict__empty">No matches yet. Try a year, make, or part name.</div>') + '<a class="bbp-predict__all" href="' + url + '">See all ' + hits.length + ' results for ' + esc(label) + ' →</a>';
      box.hidden = false;
    });
  }

  /* =====================================================================
     6. Product page, cart, sheets, home rail, recommendations
     ===================================================================== */
  function fetchCart() { return fetch(R.cart + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); }
  function cartAdd(items) { return fetch(R.cartAdd + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items }) }).then(function (r) { if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || j.message || 'Could not add'); }); return r.json(); }); }
  function cartChange(line, qty) { return fetch(R.cartChange + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line: line, quantity: qty }) }).then(function (r) { return r.json(); }); }
  function cartUpdateAttrs(attrs) { return fetch(R.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: attrs }) }).then(function (r) { return r.json(); }); }
  function updateCount(n) { $$('[data-cart-count]').forEach(function (el) { el.textContent = n; }); }

  function drawerLine(item, i) {
    var t = parseTitle(item.product_title, [], item.product_type);
    var freight = item.properties && item.properties._ship === 'freight';
    return '<div class="bbp-line" data-line="' + (i + 1) + '"><a href="' + esc(item.url) + '" class="bbp-photo-well bbp-photo bbp-photo--sq">' + (item.image ? '<img src="' + esc(imgUrl(item.image, 200)) + '" alt="">' : '') + '</a>' +
      '<div class="bbp-line__info"><div class="bbp-ptitle" data-lines="2"><div class="bbp-ptitle__meta">' + (t.years.length ? '<span class="bbp-years"><span class="bbp-years__chip">' + esc(t.yearsLabel) + '</span></span>' : '') + sideBadge(t.side, true) + '</div><span class="bbp-ptitle__name"><a href="' + esc(item.url) + '">' + esc(t.part) + '</a></span>' + (t.vehicles.length ? '<span class="bbp-ptitle__vehicles">' + esc(t.vehicles.join(' · ')) + '</span>' : '') + '</div>' +
      '<div class="bbp-line__tags"><span class="bbp-card__sku">' + esc(item.sku || '') + '</span>' + (freight ? '<span class="bbp-badge bbp-badge--freight bbp-badge--sm">' + icon('truck', 11) + 'By truck</span>' : '') + '</div>' +
      '<div class="bbp-qty"><div class="bbp-qty__box"><button type="button" class="bbp-qty__btn" data-line-delta="-1" aria-label="Decrease quantity">' + icon('minus', 14) + '</button><input class="bbp-qty__input" type="number" value="' + item.quantity + '" min="0" data-line-qty aria-label="Quantity"><button type="button" class="bbp-qty__btn" data-line-delta="1" aria-label="Increase quantity">' + icon('plus', 14) + '</button></div></div>' +
      '<button type="button" class="bbp-textbtn" data-line-remove>Remove</button></div>' +
      '<div class="bbp-line__total">' + esc(money(item.final_line_price)) + '</div></div>';
  }
  function renderDrawer(cart) {
    var items = $('[data-drawer-items]'), foot = $('[data-drawer-foot]');
    updateCount(cart.item_count);
    if (!items) return;
    if (!cart.item_count) { items.innerHTML = '<div class="bbp-drawer__empty">Nothing in the cart yet.</div>'; if (foot) foot.hidden = true; return; }
    if (foot) foot.hidden = false;
    var freight = cart.items.filter(function (it) { return it.properties && it.properties._ship === 'freight'; }).length;
    var parcel = cart.items.length - freight;
    var html = '';
    if (freight) html += '<div class="bbp-shipgroup">' + icon('truck', 16) + '<b>Truck delivery</b><span>Free to the curb, someone must sign</span></div>';
    cart.items.forEach(function (it, i) { if (it.properties && it.properties._ship === 'freight') html += drawerLine(it, i); });
    if (parcel) html += '<div class="bbp-shipgroup bbp-shipgroup--parcel">' + icon('package', 16) + '<b>Standard shipping</b><span>' + esc(S.parcelNote || '') + '</span></div>';
    cart.items.forEach(function (it, i) { if (!(it.properties && it.properties._ship === 'freight')) html += drawerLine(it, i); });
    items.innerHTML = html;
    var sub = $('[data-drawer-subtotal]'); if (sub) sub.textContent = money(cart.items_subtotal_price);
  }
  function openDrawer() { var d = $('[data-cart-drawer]'), s = $('[data-cart-scrim]'); if (!d) { window.location.href = R.cart; return; } fetchCart().then(function (c) { renderDrawer(c); d.classList.add('is-open'); if (s) s.classList.add('is-open'); document.body.classList.add('bbp-lock'); }); }
  function closeDrawer() { var d = $('[data-cart-drawer]'), s = $('[data-cart-scrim]'); if (d) d.classList.remove('is-open'); if (s) s.classList.remove('is-open'); document.body.classList.remove('bbp-lock'); }
  function bindCart() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-open-cart]')) { e.preventDefault(); openDrawer(); return; }
      if (t.closest('[data-close-cart]') || t.closest('[data-cart-scrim]')) { closeDrawer(); return; }
      var lineEl = t.closest('[data-line]');
      if (lineEl && t.closest('[data-cart-drawer]')) {
        var line = parseInt(lineEl.dataset.line, 10);
        if (t.closest('[data-line-remove]')) { cartChange(line, 0).then(renderDrawer); }
        var d = t.closest('[data-line-delta]');
        if (d) { var inp = $('[data-line-qty]', lineEl); var q = Math.max(0, (parseInt(inp.value, 10) || 0) + parseInt(d.dataset.lineDelta, 10)); cartChange(line, q).then(renderDrawer); }
      }
    });
    document.addEventListener('change', function (e) { var inp = e.target; if (inp.matches('[data-line-qty]') && inp.closest('[data-cart-drawer]')) { var lineEl = inp.closest('[data-line]'); cartChange(parseInt(lineEl.dataset.line, 10), Math.max(0, parseInt(inp.value, 10) || 0)).then(renderDrawer); } });
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches('[data-quick-add]')) return;
      e.preventDefault();
      var btn = form.querySelector('button[type="submit"]');
      var id = form.querySelector('[name="id"]').value;
      var qty = parseInt((form.querySelector('[name="quantity"]') || {}).value || '1', 10) || 1;
      var props = {}; $$('[name^="properties["]', form).forEach(function (p) { props[p.name.replace(/^properties\[(.+)\]$/, '$1')] = p.value; });
      var orig = btn ? btn.innerHTML : '';
      if (btn) { btn.disabled = true; btn.innerHTML = 'Adding'; }
      cartAdd([{ id: id, quantity: qty, properties: props }]).then(function () { if (btn) { btn.innerHTML = icon('check', 15) + 'Added'; } openDrawer(); })
        .catch(function (err) { if (btn) btn.innerHTML = esc(err.message || 'Could not add'); })
        .then(function () { setTimeout(function () { if (btn) { btn.disabled = false; btn.innerHTML = orig; } }, 1400); });
    });
    fetchCart().then(function (c) { updateCount(c.item_count); }).catch(function () {});
  }

  function initCartPage() {
    var page = $('[data-cart-page]'); if (!page) return;
    var opts = $$('[data-delivery]', page);
    var current = page.dataset.delivery || 'ship';
    function apply(m) {
      current = m; page.classList.toggle('is-pickup', m === 'pickup');
      opts.forEach(function (o) { o.classList.toggle('is-on', o.dataset.delivery === m); });
      $$('[data-show-when]', page).forEach(function (el) { el.hidden = el.dataset.showWhen !== m; });
    }
    apply(current);
    opts.forEach(function (o) { o.addEventListener('click', function () { apply(o.dataset.delivery); cartUpdateAttrs({ 'Delivery preference': o.dataset.delivery === 'pickup' ? 'Local pickup' : 'Ship' }).catch(function () {}); }); });
    page.addEventListener('click', function (e) {
      var d = e.target.closest('[data-page-delta]'); if (!d) return;
      var lineEl = d.closest('[data-line]'); var inp = $('[data-page-qty]', lineEl);
      var q = Math.max(0, (parseInt(inp.value, 10) || 0) + parseInt(d.dataset.pageDelta, 10));
      cartChange(parseInt(lineEl.dataset.line, 10), q).then(function () { window.location.reload(); });
    });
    page.addEventListener('change', function (e) { var inp = e.target; if (!inp.matches('[data-page-qty]')) return; var lineEl = inp.closest('[data-line]'); cartChange(parseInt(lineEl.dataset.line, 10), Math.max(0, parseInt(inp.value, 10) || 0)).then(function () { window.location.reload(); }); });
  }

  function initProduct() {
    var page = $('[data-pdp]'); if (!page) return;
    var qtyInput = $('[data-qty-input]', page);
    $$('[data-qty-delta]', page).forEach(function (b) { b.addEventListener('click', function () { var max = parseInt(qtyInput.max, 10) || 99; qtyInput.value = Math.min(max, Math.max(1, (parseInt(qtyInput.value, 10) || 1) + parseInt(b.dataset.qtyDelta, 10))); }); });
    $$('[data-gallery-thumb]', page).forEach(function (t) { t.addEventListener('click', function () { $$('[data-gallery-thumb]', page).forEach(function (x) { x.classList.remove('is-active'); }); t.classList.add('is-active'); var main = $('[data-gallery-main]', page); if (main) { main.src = t.dataset.galleryThumb; main.srcset = ''; } }); });
    $$('[data-tab]', page).forEach(function (b) { b.addEventListener('click', function () { $$('[data-tab]', page).forEach(function (x) { x.classList.toggle('is-active', x === b); }); $$('[data-tabpanel]', page).forEach(function (p) { p.hidden = p.dataset.tabpanel !== b.dataset.tab; }); }); });
    var form = $('[data-product-form]', page);
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[data-add-btn]'); var orig = btn ? btn.innerHTML : '';
      var props = {}; $$('[name^="properties["]', form).forEach(function (p) { props[p.name.replace(/^properties\[(.+)\]$/, '$1')] = p.value; });
      if (btn) { btn.disabled = true; btn.innerHTML = 'Adding'; }
      cartAdd([{ id: form.querySelector('[name="id"]').value, quantity: parseInt(qtyInput ? qtyInput.value : '1', 10) || 1, properties: props }])
        .then(function () { openDrawer(); }).catch(function (err) { if (btn) btn.innerHTML = esc(err.message); })
        .then(function () { setTimeout(function () { if (btn) { btn.disabled = false; btn.innerHTML = orig; } }, 1400); });
    });
    var bar = $('[data-buybar]', page), buy = $('[data-buy-anchor]', page);
    if (bar && buy && 'IntersectionObserver' in window) { var io = new IntersectionObserver(function (en) { bar.hidden = en[0].isIntersecting; }, { threshold: 0.2 }); io.observe(buy); bar.addEventListener('click', function (e) { if (e.target.closest('[data-buybar-add]') && form) { e.preventDefault(); form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true })); } }); }
    $$('[data-save-vehicle-from-product]', page).forEach(function (b) { b.addEventListener('click', function () {
      var tags = (b.dataset.tags || '').split('|').filter(Boolean); var make = b.dataset.make || '';
      var years = (b.dataset.years || '').split(' ').filter(Boolean);
      loadIndex().then(function (idx) {
        for (var i = 0; i < tags.length; i++) {
          var m = TAG_RE.exec(tags[i].trim()); if (!m) continue;
          var model = m[1].trim(), mk = make; var fw = model.split(/\s+/)[0];
          var brand = BRANDS.filter(function (x) { return x.toLowerCase() === fw.toLowerCase(); })[0]; if (brand) { mk = BRAND_DISPLAY[brand] || brand; model = model.slice(fw.length).trim(); }
          if (mk === 'Ram') mk = 'Dodge';
          var y2 = years.length ? years[years.length - 1] : (m[3] || m[2]);
          if (idx && idx.makeModels[mk] && idx.makeModels[mk].indexOf(model) === -1) continue;
          saveVehicle({ year: String(y2), make: mk, model: model, trim: '' }); applyVehicle();
          b.textContent = 'Saved ' + vehicleLabel({ year: y2, make: mk, model: model });
          return;
        }
        openSheet();
      });
    }); });
    loadIndex().then(function (idx) { renderBanners(idx); });
  }

  function openSheet() { var sh = $('[data-vehicle-sheet]'); if (!sh) return; initSelectors(sh); renderGarage(); sh.classList.add('is-open'); var sc = $('[data-sheet-scrim]', sh); if (sc) sc.classList.add('is-open'); document.body.classList.add('bbp-lock'); var y = $('[data-vs-year]', sh); if (y) setTimeout(function () { y.focus(); }, 50); }
  function closeSheet() { var sh = $('[data-vehicle-sheet]'); if (sh) sh.classList.remove('is-open'); var sc = $('[data-sheet-scrim]'); if (sc) sc.classList.remove('is-open'); var f = $('[data-facets-panel]'); if (f) f.classList.remove('is-open'); var m = $('[data-mobile-nav]'); if (m) m.classList.remove('is-open'); document.body.classList.remove('bbp-lock'); }
  function bindSheets() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-open-vehicle]')) { e.preventDefault(); openSheet(); return; }
      if (t.closest('[data-sheet-close]') || t.closest('[data-sheet-scrim]')) { e.preventDefault(); closeSheet(); return; }
      if (t.closest('[data-clear-vehicle]')) { e.preventDefault(); clearVehicle(); applyVehicle(); return; }
      var use = t.closest('[data-garage-use]'); if (use) { var g = state.garage.filter(function (x) { return vehicleId(x) === use.dataset.garageUse; })[0]; if (g) { saveVehicle(g); closeSheet(); applyVehicle(); } return; }
      var del = t.closest('[data-garage-delete]'); if (del) { deleteGarage(del.dataset.garageDelete); applyVehicle(); return; }
      if (t.closest('[data-menu-toggle]')) { var m = $('[data-mobile-nav]'); if (m) { m.classList.add('is-open'); document.body.classList.add('bbp-lock'); } return; }
      if (t.closest('[data-menu-close]')) { closeSheet(); return; }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') { closeSheet(); closeDrawer(); } });
  }

  function renderFitsRail(idx) {
    var rail = $('[data-fits-rail]'); if (!rail) return;
    var v = state.vehicle, grid = $('[data-rail-grid]', rail), fallback = $('[data-rail-fallback]', rail), title = $('[data-rail-title]', rail), seeAll = $('[data-rail-all]', rail);
    if (!v || !idx) { if (fallback) fallback.hidden = false; if (grid) { grid.hidden = true; grid.innerHTML = ''; } if (title) title.textContent = title.dataset.defaultTitle || title.textContent; return; }
    var fs = fitSetFor(idx, v);
    var handles = Array.from(fs);
    if (!handles.length) { if (fallback) fallback.hidden = false; if (grid) grid.hidden = true; return; }
    if (title) title.textContent = 'Fits your ' + vehicleLabel(v, true);
    if (seeAll) seeAll.href = (R.search || '/search') + '?type=product&q=' + encodeURIComponent([v.year, v.make, v.model].join(' '));
    loadMeta().then(function (meta) {
      var order = handles.slice().sort(function (a, b) { var ma = meta && meta[a] || {}, mb = meta && meta[b] || {}; if (!!ma.av !== !!mb.av) return ma.av ? -1 : 1; var oa = PART_ORDER.indexOf(ma.pp), ob = PART_ORDER.indexOf(mb.pp); if (oa < 0) oa = 99; if (ob < 0) ob = 99; return oa - ob; });
      pool(order.slice(0, 8), fetchProduct, 6).then(function (items) {
        var ps = items.filter(Boolean); if (!ps.length) return;
        if (fallback) fallback.hidden = true;
        grid.hidden = false; grid.innerHTML = ps.map(cardHTML).join('');
        paintFitment(idx, grid);
      });
    });
  }

  function initRecommendations() {
    var host = $('[data-recommendations]'); if (!host) return;
    var url = host.dataset.url; if (!url) return;
    fetch(url).then(function (r) { return r.ok ? r.text() : ''; }).then(function (html) {
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var inner = doc.querySelector('[data-recommendations-inner]');
      if (inner && inner.children.length) { host.innerHTML = inner.innerHTML; host.hidden = false; loadIndex().then(function (idx) { paintFitment(idx, host); }); }
    }).catch(function () {});
  }

  // Trade form: tax-exempt ordering genuinely requires a tax ID, so the checkbox
  // makes the field required rather than just revealing a note.
  function initTradeForm() {
    var form = $('#trade-form'); if (!form) return;
    var box = $('[data-tax-exempt]', form), taxId = $('[data-tax-id]', form),
        note = $('[data-tax-note]', form.closest('.bbp-form') || form);
    if (!box || !taxId) return;
    var label = taxId.closest('.bbp-field');
    function sync() {
      var on = box.checked;
      taxId.required = on;
      if (note) note.hidden = !on;
      if (label) label.classList.toggle('is-required', on);
      taxId.setAttribute('aria-required', on ? 'true' : 'false');
      if (on) taxId.setCustomValidity(taxId.value.trim() ? '' : 'Enter the federal tax ID for tax-exempt ordering.');
      else taxId.setCustomValidity('');
    }
    box.addEventListener('change', function () { sync(); if (box.checked) taxId.focus(); });
    taxId.addEventListener('input', function () { taxId.setCustomValidity(''); });
    sync();
  }

  function initVehicleSelectorsFromForms() {
    $$('form [data-vehicle-field]').forEach(function (inp) { if (!inp.value && state.vehicle) inp.value = vehicleLabel(state.vehicle); });
  }

  /* =====================================================================
     7. Manufacturer browser: make -> model -> year -> part type
     Reads the same fitment index the vehicle selector uses, so it can never
     offer a combination the catalog cannot fill. Ends on the exact-fit
     results page. Degrades to plain collection links with no JavaScript.
     ===================================================================== */
  var mbCache = null;
  function mbBuild(idx) {
    if (mbCache) return mbCache;
    var makes = {};
    Object.keys(idx.fitment || {}).forEach(function (year) {
      var byMake = idx.fitment[year] || {};
      Object.keys(byMake).forEach(function (mk) {
        var m = makes[mk] || (makes[mk] = { handles: {}, models: {} });
        var byModel = byMake[mk] || {};
        Object.keys(byModel).forEach(function (mdl) {
          var md = m.models[mdl] || (m.models[mdl] = { handles: {}, years: {} });
          md.years[year] = 1;
          (byModel[mdl] || []).forEach(function (h) { md.handles[h] = 1; m.handles[h] = 1; });
        });
      });
    });
    mbCache = makes;
    return makes;
  }
  function mbYearsOf(md) { return Object.keys(md.years).sort(function (a, b) { return Number(b) - Number(a); }); }
  function mbRange(md) {
    var ys = mbYearsOf(md); if (!ys.length) return '';
    var lo = ys[ys.length - 1], hi = ys[0];
    return lo === hi ? lo : lo + '–' + hi;
  }
  // The tile count has to be what the results page will actually show, not the
  // count of products whose type is exactly this one. The search page matches on
  // keywords, so "Grille" also brings in Grille Trim and Outer Grille. Counting
  // the same way keeps the number on the tile honest.
  function mbLinkCount(handles, pp, meta) {
    var kws = String(pp).toLowerCase().split(/[^a-z0-9-]+/).filter(function (t) { return t.length >= 2 && !STOPWORDS[t]; });
    var n = 0;
    handles.forEach(function (h) { var m = meta[h]; if (m && metaMatches(m, kws)) n++; });
    return n;
  }
  function mbPartGroups(handles, meta) {
    var groups = {};
    handles.forEach(function (h) {
      var m = meta && meta[h]; if (!m) return;
      var k = m.pp || m.ty || 'Other';
      (groups[k] = groups[k] || []).push(h);
    });
    var names = Object.keys(groups).sort(function (a, b) {
      var oa = PART_ORDER.indexOf(a), ob = PART_ORDER.indexOf(b);
      if (oa < 0) oa = 99; if (ob < 0) ob = 99;
      if (oa !== ob) return oa - ob;
      return groups[b].length - groups[a].length;
    });
    return { names: names, groups: groups };
  }

  function initMakeBrowser() {
    var root = $('[data-make-browser]'); if (!root) return;
    var panel = $('[data-mb-panel]', root), crumbs = $('[data-mb-crumbs]', root),
        body = $('[data-mb-body]', root), closeBtn = $('[data-mb-close]', root);
    var tiles = $$('[data-mb-make]', root);
    var idx = null, meta = null, data = null, hashLock = false;
    var st = { make: null, label: null, model: null, year: null };

    function labelFor(mk) {
      var t = $('[data-mb-make="' + String(mk).replace(/"/g, '') + '"]', root);
      return (t && t.dataset.mbLabel) || mk;
    }
    function searchUrl(parts) {
      return (R.search || '/search') + '?type=product&q=' + encodeURIComponent(parts.filter(Boolean).join(' '));
    }
    function setHash() {
      var frag = [st.make, st.model, st.year].filter(Boolean).map(encodeURIComponent).join('/');
      hashLock = true;
      if (frag) location.hash = 'browse=' + frag;
      else if (location.hash) location.hash = 'browse';
      setTimeout(function () { hashLock = false; }, 0);
    }
    function readHash() {
      var m = /(?:^|#|&)browse=([^&]*)/.exec(location.hash || '');
      if (!m) return null;
      var p = m[1].split('/').map(decodeURIComponent);
      return { make: p[0] || null, model: p[1] || null, year: p[2] || null };
    }

    function grid(items, cls) {
      return '<div class="bbp-mb__grid' + (cls ? ' ' + cls : '') + '">' + items.join('') + '</div>';
    }
    function opt(attrs, tag, title, sub, count) {
      return '<' + tag + ' class="bbp-mb__opt"' + attrs + '>' +
        '<span class="bbp-mb__opt-t">' + esc(title) + '</span>' +
        (sub ? '<span class="bbp-mb__opt-s">' + esc(sub) + '</span>' : '') +
        (count != null ? '<span class="bbp-mb__opt-n">' + count + '</span>' : '') +
        '</' + tag + '>';
    }
    function pick(kind, value, title, sub, count) {
      return opt(' type="button" data-mb-pick="' + kind + '" data-mb-value="' + esc(value) + '"', 'button', title, sub, count);
    }
    function link(href, title, sub, count) {
      return opt(' href="' + esc(href) + '"', 'a', title, sub, count);
    }

    function renderCrumbs() {
      var steps = [{ label: st.label || st.make, level: 'make' }];
      if (st.model) steps.push({ label: st.model, level: 'model' });
      if (st.year) steps.push({ label: st.year === 'any' ? 'Any year' : st.year, level: 'year' });
      var last = steps.length - 1;
      crumbs.innerHTML = '<button type="button" class="bbp-mb__crumb" data-mb-to="root">' + icon('car-front', 14) + 'All makes</button>' +
        steps.map(function (s, i) {
          return '<span class="bbp-mb__sep">' + icon('chevron-right', 12) + '</span>' +
            (i === last
              ? '<span class="bbp-mb__crumb is-current">' + esc(s.label) + '</span>'
              : '<button type="button" class="bbp-mb__crumb" data-mb-to="' + s.level + '">' + esc(s.label) + '</button>');
        }).join('');
    }

    function render() {
      tiles.forEach(function (t) { t.classList.toggle('is-active', !!st.make && t.dataset.mbMake === st.make); });
      if (!st.make || !data || !data[st.make]) { panel.hidden = true; return; }
      var entry = data[st.make];
      panel.hidden = false;
      renderCrumbs();
      var name = st.label || st.make;

      // Step 2, model.
      if (!st.model) {
        var models = Object.keys(entry.models).sort();
        body.innerHTML = '<p class="bbp-mb__step">Step 2 of 4 &middot; Pick the model</p>' +
          grid(models.map(function (mdl) {
            var md = entry.models[mdl];
            return pick('model', mdl, mdl, mbRange(md), Object.keys(md.handles).length);
          })) +
          '<div class="bbp-mb__skip">' + link(searchUrl([st.make]), 'Show all ' + name + ' parts', '', Object.keys(entry.handles).length) + '</div>';
        return;
      }

      var md = entry.models[st.model];
      if (!md) { st.model = null; render(); return; }

      // Step 3, year.
      if (!st.year) {
        body.innerHTML = '<p class="bbp-mb__step">Step 3 of 4 &middot; Pick the year</p>' +
          grid(mbYearsOf(md).map(function (y) {
            var n = (((idx.fitment[y] || {})[st.make] || {})[st.model] || []).length;
            return pick('year', y, y, '', n);
          }), 'bbp-mb__grid--tight') +
          '<div class="bbp-mb__skip">' + pick('year', 'any', 'Any year', 'Every ' + name + ' ' + st.model, Object.keys(md.handles).length) + '</div>';
        return;
      }

      // Step 4, part type.
      var yr = st.year === 'any' ? null : st.year;
      var handles;
      if (yr) {
        var seen = {};
        (((idx.fitment[yr] || {})[st.make] || {})[st.model] || []).forEach(function (h) { seen[h] = 1; });
        handles = Object.keys(seen);
      } else {
        handles = Object.keys(md.handles);
      }
      var vehicle = [yr, st.make, st.model];
      if (!meta) {
        body.innerHTML = '<p class="bbp-mb__step">Step 4 of 4</p><div class="bbp-mb__skip">' +
          link(searchUrl(vehicle), 'Show all parts that fit', '', handles.length) + '</div>';
        return;
      }
      var pg = mbPartGroups(handles, meta);
      body.innerHTML = '<p class="bbp-mb__step">Step 4 of 4 &middot; Pick the part</p>' +
        (pg.names.length
          ? grid(pg.names.map(function (pp) { return link(searchUrl(vehicle.concat([pp])), pp, '', mbLinkCount(handles, pp, meta)); }))
          : '<p class="bbp-mb__empty">Nothing listed for this combination yet. Try another year.</p>') +
        '<div class="bbp-mb__skip">' + link(searchUrl(vehicle), 'Show all ' + handles.length + ' parts that fit', '', null) + '</div>';
    }

    function go(next, push) {
      st = next;
      render();
      if (push !== false) setHash();
      if (st.make && st.model && st.year && st.year !== 'any') {
        saveVehicle({ year: st.year, make: st.make, model: st.model, trim: '' });
        applyVehicle();
      }
      if (st.make && !panel.hidden) {
        var r = panel.getBoundingClientRect();
        if (r.top < 0 || r.bottom > window.innerHeight) panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    Promise.all([loadIndex(), loadMeta()]).then(function (res) {
      idx = res[0]; meta = res[1];
      if (!idx) return;                        // no index, badges stay ordinary links
      data = mbBuild(idx);
      root.dataset.mbReady = '1';

      tiles.forEach(function (t) {
        var mk = t.dataset.mbMake, entry = data[mk];
        if (!entry) { t.hidden = true; return; }
        var n = Object.keys(entry.handles).length;
        var c = $('[data-mb-count]', t);
        if (c) { c.textContent = n.toLocaleString() + (n === 1 ? ' part' : ' parts'); c.hidden = false; }
        t.addEventListener('click', function (e) {
          e.preventDefault();
          if (st.make === mk) { go({ make: null, label: null, model: null, year: null }); return; }
          go({ make: mk, label: t.dataset.mbLabel || mk, model: null, year: null });
        });
      });

      body.addEventListener('click', function (e) {
        var b = e.target.closest('[data-mb-pick]'); if (!b) return;
        var v = b.dataset.mbValue;
        if (b.dataset.mbPick === 'model') go({ make: st.make, label: st.label, model: v, year: null });
        else go({ make: st.make, label: st.label, model: st.model, year: v });
      });

      crumbs.addEventListener('click', function (e) {
        var b = e.target.closest('[data-mb-to]'); if (!b) return;
        var to = b.dataset.mbTo;
        if (to === 'root') go({ make: null, label: null, model: null, year: null });
        else if (to === 'make') go({ make: st.make, label: st.label, model: null, year: null });
        else if (to === 'model') go({ make: st.make, label: st.label, model: st.model, year: null });
      });

      if (closeBtn) closeBtn.addEventListener('click', function () { go({ make: null, label: null, model: null, year: null }); });

      window.addEventListener('hashchange', function () {
        if (hashLock) return;
        var h = readHash();
        if (h && h.make && data[h.make]) go({ make: h.make, label: labelFor(h.make), model: h.model, year: h.year }, false);
        else go({ make: null, label: null, model: null, year: null }, false);
      });

      var initial = readHash();
      if (initial && initial.make && data[initial.make]) {
        go({ make: initial.make, label: labelFor(initial.make), model: initial.model, year: initial.year }, false);
      }
    });
  }

  /* =====================================================================
     Init
     ===================================================================== */
  document.addEventListener('DOMContentLoaded', function () {
    renderChips(); renderGarage();
    initSelectors(document);
    bindSheets(); bindCart();
    initListing(); initPredictive();
    initProduct(); initCartPage(); initRecommendations();
    initMakeBrowser();
    initVehicleSelectorsFromForms();
    initTradeForm();
    loadIndex().then(function (idx) { renderBanners(idx); paintFitment(idx, document); renderFitsRail(idx); });
    // Warm the index during idle time on pages that will need it next.
    if ('requestIdleCallback' in window) requestIdleCallback(function () { loadIndex(); loadMeta(); });
  });

  window.BBPTheme = { state: state, saveVehicle: saveVehicle, clearVehicle: clearVehicle, applyVehicle: applyVehicle, openSheet: openSheet, openDrawer: openDrawer, parseTitle: parseTitle, parseQuery: parseQuery, loadIndex: loadIndex, loadMeta: loadMeta };
})();
